import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import axios from "axios";
import { useSelector } from "react-redux";
import { Row, Col } from "react-bootstrap";
import { FiLayers, FiMapPin, FiFileText, FiSend, FiArrowLeft, FiCheck } from "react-icons/fi";
import Notification, { showToast } from "../../../../components/common/notification/Notification";
import styles from "./AddPostForm.module.css";

const MAX_DESCRIPTION_LENGTH = 2000;

const EditPostForm = () => {
  const { Id } = useParams();
  const [postData, setPostData] = useState(null);
  const [services, setServices] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [description, setDescription] = useState("");
  const [characterCount, setCharacterCount] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const baseUrl = process.env.REACT_APP_BASE_URL;
  const user = useSelector((state) => state.auth.user);
  const token = user?.refreshToken;
  const navigate = useNavigate();

  // Fetch dropdown data and post data
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const serviceRes = await axios.get(`${baseUrl}/all/services`);
        setServices(serviceRes.data.services);

        const stateRes = await axios.post(
          `${baseUrl}/admin/states/1`,
          { page_number: 1, page_size: 20, order_by: "ASC" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setStates(stateRes.data.states);
      } catch (err) {
        console.error("Error fetching dropdowns:", err);
      }
    };

    const fetchPostData = async () => {
      try {
        const res = await axios.get(`${baseUrl}/property/lead/id/${Id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = res.data.data;
        setPostData(data);
        setDescription(data.description || "");
        setCharacterCount((data.description || "").length);

        if (data.state_name) {
          const stateRes = await axios.post(
            `${baseUrl}/admin/states/1`,
            { page_number: 1, page_size: 20, order_by: "ASC" },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const matchedState = stateRes.data.states.find(
            (s) => s.name === data.state_name
          );
          if (matchedState) {
            fetchCities(matchedState.id);
          }
        }
      } catch (err) {
        console.error("Error fetching post data:", err);
      }
    };

    fetchDropdowns();
    if (Id) fetchPostData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl, token, Id]);

  const fetchCities = async (stateId) => {
    if (!stateId) {
      setCities([]);
      return;
    }
    try {
      const res = await axios.post(
        `${baseUrl}/admin/cities/${stateId}`,
        { page_number: 1, page_size: 20, order_by: "ASC" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCities(res.data.cities || []);
    } catch {
      setCities([]);
    }
  };

  const validationSchema = Yup.object({
    service_id: Yup.string().required("Service is required"),
    state: Yup.string().required("State is required"),
    city: Yup.string().required("City is required"),
    description: Yup.string()
      .required("Description is required")
      .max(MAX_DESCRIPTION_LENGTH, "Description too long"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    setPreviewData(values);
    setShowPreview(true);
    setSubmitting(false);
  };

  const handleFinalSubmit = async () => {
    try {
      const currentUserId = user?.id || user?.user?.id || user?.userDetails?.id;
      const payload = {
        id: postData.id,
        service: previewData.service,
        state_name: previewData.state_name,
        city_name: previewData.city_name,
        description: previewData.description,
        property_status_id: postData?.property_status_id || 1,
        property_type_id: postData?.property_type_id || 1,
        purpose_id: postData?.purpose_id || 1,
        user_id: currentUserId,
      };

      const response = await axios.put(
        `${baseUrl}/property/lead/edit`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.statusType === "SUCCESS") {
        showToast("Post updated successfully", { type: "success" });
        setTimeout(() => {
          navigate("/post");
        }, 1500);
      } else {
        showToast(response.data.text || "Failed to update Post data", { type: "error" });
      }
    } catch (err) {
      showToast("Failed to update Post data", { type: "error" });
      console.log(err);
    }
  };

  if (!postData) {
    return (
      <div className={styles.container} style={{ minHeight: "400px" }}>
        <p className="text-muted">Loading post data…</p>
      </div>
    );
  }

  const matchedService = services.find((s) => s.name === postData.service);
  const matchedState = states.find((s) => s.name === postData.state_name);
  const matchedCity = cities.find((c) => c.name === postData.city_name);

  return (
    <div className={styles.container}>
      <Notification />
      <div className={styles.headerBlock}>
        <h1 className={styles.title}>Edit Post</h1>
        <p className={styles.subtitle}>Update your post details and information below.</p>
      </div>

      <div className={styles.card}>
        <Formik
          enableReinitialize
          initialValues={{
            service_id: matchedService ? matchedService.id : "",
            service: postData.service || "",
            state: matchedState ? matchedState.id : "",
            state_name: postData.state_name || "",
            city: matchedCity ? matchedCity.id : "",
            city_name: postData.city_name || "",
            description: postData.description || "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ setFieldValue, values, isSubmitting }) =>
            showPreview ? (
              <div className="preview-section">
                <h3 className="fw-bold mb-3" style={{ color: "#0f172a" }}>Preview Your Post Update</h3>
                <div className={styles.previewCard}>
                  <div className="mb-2">
                    <strong>Service:</strong> {previewData.service}
                  </div>
                  <div className="mb-2">
                    <strong>Location:</strong> {previewData.city_name}, {previewData.state_name}
                  </div>
                  <div className="mb-2">
                    <strong>Description:</strong>
                    <div
                      dangerouslySetInnerHTML={{ __html: previewData.description }}
                      className="p-3 bg-white border rounded mt-2"
                    />
                  </div>
                </div>
                <div className="d-flex justify-content-between mt-4">
                  <button type="button" className={styles.btnBack} onClick={() => setShowPreview(false)}>
                    <FiArrowLeft size={16} /> Back to Edit
                  </button>
                  <button type="button" className={styles.btnSubmit} onClick={handleFinalSubmit}>
                    <FiCheck size={16} /> Confirm & Update
                  </button>
                </div>
              </div>
            ) : (
              <Form>
                <Row className="g-4 mb-4">
                  <Col md={4}>
                    <div className="form-group">
                      <label htmlFor="service_id" className={styles.fieldLabel}>
                        <FiLayers size={14} /> Service
                      </label>
                      <Field
                        as="select"
                        name="service_id"
                        id="service_id"
                        className={styles.selectInput}
                        value={values.service_id}
                        onChange={(e) => {
                          const selectedOption = e.target.selectedOptions[0];
                          const name = selectedOption.getAttribute("data-name");
                          setFieldValue("service_id", e.target.value);
                          setFieldValue("service", name);
                        }}
                      >
                        <option value="">Select a service</option>
                        {services.map((srv) => (
                          <option key={srv.id} value={srv.id} data-name={srv.name}>
                            {srv.name}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage name="service_id" component="div" className="text-danger mt-1 small" />
                    </div>
                  </Col>

                  <Col md={4}>
                    <div className="form-group">
                      <label htmlFor="state" className={styles.fieldLabel}>
                        <FiMapPin size={14} /> State
                      </label>
                      <Field
                        as="select"
                        name="state"
                        id="state"
                        className={styles.selectInput}
                        value={values.state}
                        onChange={async (e) => {
                          const selectedOption = e.target.selectedOptions[0];
                          const name = selectedOption.getAttribute("data-name");
                          const stateId = e.target.value;
                          setFieldValue("state", stateId);
                          setFieldValue("state_name", name);
                          await fetchCities(stateId);
                          setFieldValue("city", "");
                          setFieldValue("city_name", "");
                        }}
                      >
                        <option value="">Select a state</option>
                        {states.map((state) => (
                          <option key={state.id} value={state.id} data-name={state.name}>
                            {state.name}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage name="state" component="div" className="text-danger mt-1 small" />
                    </div>
                  </Col>

                  <Col md={4}>
                    <div className="form-group">
                      <label htmlFor="city" className={styles.fieldLabel}>
                        <FiMapPin size={14} /> City
                      </label>
                      <Field
                        as="select"
                        name="city"
                        id="city"
                        className={styles.selectInput}
                        value={values.city}
                        onChange={(e) => {
                          const selectedOption = e.target.selectedOptions[0];
                          const name = selectedOption.getAttribute("data-name");
                          setFieldValue("city", e.target.value);
                          setFieldValue("city_name", name);
                        }}
                      >
                        <option value="">Select a city</option>
                        {cities.map((city) => (
                          <option key={city.id} value={city.id} data-name={city.name}>
                            {city.name}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage name="city" component="div" className="text-danger mt-1 small" />
                    </div>
                  </Col>
                </Row>

                <div className="mb-4">
                  <label htmlFor="description" className={styles.fieldLabel}>
                    <FiFileText size={14} /> Description
                  </label>
                  <div className={styles.quillWrapper}>
                    <ReactQuill
                      theme="snow"
                      value={description}
                      onChange={(value) => {
                        setDescription(value);
                        setFieldValue("description", value);
                        setCharacterCount(value.length);
                      }}
                    />
                  </div>
                  <div className={styles.charCounter}>
                    <span>{characterCount}/{MAX_DESCRIPTION_LENGTH} characters</span>
                    {characterCount > MAX_DESCRIPTION_LENGTH && (
                      <span className="text-danger font-semibold">Description too long</span>
                    )}
                  </div>
                  <ErrorMessage name="description" component="div" className="text-danger mt-1 small" />
                </div>

                <div className="d-flex justify-content-between align-items-center mt-4">
                  <Link to="/post" className={styles.btnBack}>
                    <FiArrowLeft size={16} /> Back
                  </Link>
                  <button
                    type="submit"
                    className={styles.btnSubmit}
                    disabled={isSubmitting || characterCount > MAX_DESCRIPTION_LENGTH}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                        Updating…
                      </>
                    ) : (
                      <>
                        <FiSend size={16} /> Update Post
                      </>
                    )}
                  </button>
                </div>
              </Form>
            )
          }
        </Formik>
      </div>
    </div>
  );
};

export default EditPostForm;